import AlertDetails from 'features/Weather/features/WeatherAlerts/features/AlertList/features/AlertCard/components/AlertDetails/AlertDetails';
import AlertMap from 'features/Weather/features/WeatherAlerts/features/AlertList/features/AlertCard/components/AlertMap/AlertMap';
import {
  getAlertSender,
  getAlertSource,
} from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import { getAlertSeverityLabel } from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import useWindow from 'hooks/useWindow';
import { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp } from 'react-icons/fa6';
import { formatDateTimeRange } from 'utils/dates/date.utils';
import styles from './AlertCard.module.css';

/**
 * @param {{ alert: Alert, renderActions?: React.ReactNode, renderDetails?: React.ReactNode }} props
 */
const AlertCard = ({ alert, renderActions, renderDetails }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { width } = useWindow();
  const severityLabel = alert.severityLabel ?? getAlertSeverityLabel(alert.severity);
  const severityClassName = `severity-${severityLabel.toLowerCase().replace(/\s+/g, '-')}`;
  const source = getAlertSource(alert);
  const sender = getAlertSender(alert);

  return (
    <div className={`${styles['alert-card']} d-flex mt-3 w-100 overflow-hidden bg-primary`}>
      <div className={`${styles['alert-severity-tag']} ${styles[severityClassName]}`}>
        <dl className="m-0">
          <dt className="visually-hidden">Severity</dt>
          <dd className={`${styles['alert-severity-label']} m-0`}>{severityLabel}</dd>
        </dl>
      </div>
      <div className={`${styles['alert-card-body']} min-w-0 flex-grow-1 position-relative`}>
        {renderActions ? (
          <div
            className="position-absolute top-0 end-0 m-2"
            onClick={(event) => event.stopPropagation()}
          >
            {renderActions}
          </div>
        ) : null}
        <div className="w-100">
          <div
            onClick={() => setShowInfo(!showInfo)}
            className={`d-flex flex-column flex-lg-row w-100 align-items-center cursor-pointer gap-3 ${
              showInfo ? `border-bottom pb-3 ${styles['alert-detail-boundary']}` : ''
            }`}
          >
            {/* Card Info */}
            <div
              className={`${styles['alert-information']} text-center text-lg-start flex-grow-1 min-w-0`}
            >
              <div className={`${styles['alert-title']} fw-bold`}>{alert.description}</div>
              <div
                className={`${styles['alert-badges']} d-flex align-items-center gap-2 flex-wrap`}
              >
                {source.value === 'fusion' && (alert.updatedbyId != null || alert.updated_at) ? (
                  <Badge bg="info">Updated</Badge>
                ) : null}
                <Badge bg={null} className={styles['alert-source-badge']}>
                  {source.label}
                </Badge>
                {alert.statusLabel ? (
                  <Badge bg={alert.statusBadgeVariant ?? 'secondary'}>{alert.statusLabel}</Badge>
                ) : null}
                {/* FEMA class badge. Only a FEMA card with a known class sets the label. */}
                {alert.environmentLabel ? (
                  <Badge
                    bg={alert.environmentBadgeVariant ?? 'secondary'}
                    data-testid="alert-environment-badge"
                  >
                    {alert.environmentLabel}
                  </Badge>
                ) : null}
                {alert.globalActionId != null ? (
                  <Badge bg="dark">
                    {alert.globalActionTitle
                      ? `Global Action: ${alert.globalActionTitle}`
                      : `Global Action ${alert.globalActionId}`}
                  </Badge>
                ) : null}
              </div>
              <div className={styles['alert-metadata']}>{sender}</div>
              {alert.areaDescription ? (
                <div className={styles['alert-metadata']}>{alert.areaDescription}</div>
              ) : null}
              <div className={styles['alert-metadata']}>
                In Effect{' '}
                {formatDateTimeRange(alert.issue_time_utc, alert.valid_until_time_utc, '2-digit')}
              </div>
              <div className={styles['alert-metadata']}>
                {alert.processedCenter
                  ? `Coordinates: ${Number(alert.processedCenter[0]).toFixed(5)}° N, ${Number(alert.processedCenter[1]).toFixed(5)}° W`
                  : 'No Coordinates Provided'}
              </div>
            </div>
            {/* Map */}
            <AlertMap alert={alert} />
            {/* Buttons */}
            <div
              className="d-flex flex-column flex-lg-row position-relative flex-shrink-0"
              style={{ width: width < 992 && '100%', minWidth: width >= 992 ? 56 : undefined }}
            >
              {width >= 992 ? (
                <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                  {showInfo ? <FaChevronLeft size={40} /> : <FaChevronRight size={40} />}
                </Button>
              ) : (
                <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                  {showInfo ? <FaChevronUp size={30} /> : <FaChevronDown size={30} />}
                </Button>
              )}
            </div>
          </div>
        </div>
        {showInfo && (
          <div className={`border rounded-2 mt-3 p-3 ${styles['alert-detail-boundary']}`}>
            {renderDetails ?? <AlertDetails alert={alert} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertCard;
